import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarIcon, CheckIcon, XIcon } from 'lucide-react';
import { Button } from './Button';
import { contact } from '../data/content';
import { track } from '../utils/tracking';
interface BookingModalProps {
  open: boolean;
  onClose: () => void;
}
const slots = ['Tomorrow, 9:00 AM', 'Tomorrow, 1:30 PM', 'Thursday, 10:00 AM', 'Thursday, 3:45 PM'];
export function BookingModal({
  open,
  onClose
}: BookingModalProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [booked, setBooked] = useState(false);
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setBooked(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const confirm = () => {
    if (!selected) return;
    track('calendar_booking', {
      slot: selected
    });
    setBooked(true);
  };
  return <AnimatePresence data-id="element-27" data-mp="2fbrxm4hqce71">
      {open && <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" data-id="element-28" data-mp="2fagrhy0taucr">
          <motion.button type="button" aria-label="Close booking" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-0 bg-ink/60" data-id="element-29" data-mp="21axkx9nnfr2k" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby="booking-title" initial={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative m-3 w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl md:p-7" data-id="element-30" data-mp="1ilhk0l81xwlm">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg text-ink-400 transition-colors duration-150 ease-smooth hover:bg-canvas hover:text-ink" data-id="element-31" data-mp="uj43aj935mxc">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-32" data-mp="fqsktwoavp1n" />
            </button>

            {booked ? <div role="status" aria-live="polite" data-id="element-33" data-mp="1deay9kswpfbz">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-soft" data-id="element-34" data-mp="18ivnx2u3y1kb">
                  <CheckIcon className="h-5 w-5 text-accent" aria-hidden="true" data-id="element-35" data-mp="13gwjrqqjn0mb" />
                </span>
                <h2 id="booking-title" className="mt-4 font-display text-[22px] font-semibold tracking-[-0.02em]" data-id="element-36" data-mp="yiv0b4p7gzab">
                  Your call is requested for {selected}.
                </h2>
                <p className="mt-3 text-[15px] leading-relaxed text-ink-600" data-id="element-37" data-mp="3j82b3yx092kj">
                  A HomeSmart NEXT team member will confirm by email and text. Questions before then? Call or text{' '}
                  {contact.phone}.
                </p>
                <div className="mt-6" data-id="element-38" data-mp="28q0r9t0ste2g">
                  <Button onClick={onClose} fullWidth className="sm:w-auto" data-id="element-39" data-mp="28oz78z1t1h8l">
                    Done
                  </Button>
                </div>
              </div> : <>
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-soft" data-id="element-40" data-mp="2if0tdlalkpkv">
                  <CalendarIcon className="h-5 w-5 text-accent" aria-hidden="true" data-id="element-41" data-mp="2mfccuur84jur" />
                </span>
                <h2 id="booking-title" className="mt-4 font-display text-[22px] font-semibold leading-tight tracking-[-0.02em] text-ink" data-id="element-42" data-mp="2rh1sknpe5lp6">
                  Book a private growth call
                </h2>
                <p className="mt-2 text-[15px] leading-relaxed text-ink-600" data-id="element-43" data-mp="120iu34tsm5lh">
                  A 20-minute conversation about your business, your market, and where better systems could help.
                </p>

                {/* Calendar embed placeholder: replace the slot list with the scheduling embed. */}
                <fieldset className="mt-5" data-id="element-44" data-mp="31hsz56kenk9q">
                  <legend className="text-[12px] font-semibold uppercase tracking-[0.12em] text-ink-400" data-id="element-45" data-mp="31grjeosg29lo">
                    [Calendar embed placeholder] Available times
                  </legend>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-46" data-mp="29pk1w0grqnw3">
                    {slots.map(slot => <label key={slot} className={`flex h-12 cursor-pointer items-center gap-2 rounded-lg border px-3.5 text-[15px] font-medium transition-colors duration-150 ease-smooth ${selected === slot ? 'border-accent bg-accent-soft text-ink' : 'border-line text-ink-600'}`} data-id="element-47" data-mp="2ijzv20y1p4ar">
                        <input type="radio" name="slot" value={slot} checked={selected === slot} onChange={() => setSelected(slot)} className="h-4 w-4 accent-[#ce0e2d]" data-id="element-48" data-mp="1byore17xx7do" />
                        {slot}
                      </label>)}
                  </div>
                </fieldset>

                <div className="mt-6" data-id="element-49" data-mp="1znzmb22m07jw">
                  <Button onClick={confirm} disabled={!selected} fullWidth data-id="element-50" data-mp="1zmyd7355lv4t">
                    Confirm This Time
                  </Button>
                </div>
              </>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}